import {
  autoOutline,
  blank,
  discFill,
  fromCells,
  outlineRect,
  plot,
  rect,
  thickLine,
  toCells,
  type Cells,
} from '../compose';
import type { PaletteInstance, SpriteDef } from '../types';

/**
 * Ranch and track scenery. Structural pixel art: painted onto cell
 * grids with rect/disc/line helpers, hand-detailed, auto-outlined.
 * Every sprite ships with its own palette (slot chars are per-sprite).
 */

export interface Showpiece {
  def: SpriteDef;
  palette: PaletteInstance;
}

function makeDef(
  name: string,
  cells: Cells,
  slots: string[],
  anchor: [number, number],
  extraFrames: Cells[] = [],
): SpriteDef {
  const frames = [fromCells(cells), ...extraFrames.map(fromCells)];
  return { name, w: cells[0]!.length, h: cells.length, paletteSlots: slots, frames, anchor };
}

/** Deterministic scatter — never Math.random (replay + build discipline). */
function scatter(x: number, y: number, mod: number): number {
  return ((x * 31 + y * 17 + ((x * y) % 7)) >>> 0) % mod;
}

// ---------------------------------------------------------------- barn

const BARN_SLOTS = ['outline', 'roofDark', 'roofLight', 'wallDark', 'wallMid', 'trim', 'doorDark', 'stone', 'hay'];
const BARN_PALETTE: PaletteInstance = {
  id: 'barn',
  colors: [0x241511, 0x6e2f26, 0x8a3d30, 0x8e3f2e, 0xa84e3a, 0xe8ddc8, 0x45231b, 0x6a655c, 0xd9b45a],
};

function buildBarn(): SpriteDef {
  const w = 60;
  const h = 52;
  const cells = toCells(blank(w, h));
  // Gable roof.
  for (let r = 0; r <= 15; r += 1) {
    const half = 3 + Math.round(r * 1.8);
    const left = Math.max(0, 30 - half);
    const right = Math.min(w - 1, 29 + half);
    for (let x = left; x <= right; x += 1) {
      plot(cells, x, r + 1, x < 30 ? '2' : '1');
    }
  }
  rect(cells, 27, 0, 6, 2, '2'); // ridge cap
  rect(cells, 2, 16, 56, 2, '5'); // fascia trim
  // Walls with plank seams.
  rect(cells, 4, 18, 52, 29, '4');
  for (let x = 10; x < 56; x += 7) rect(cells, x, 18, 1, 29, '3');
  // Hayloft door with spilling hay.
  rect(cells, 25, 5, 10, 9, '6');
  outlineRect(cells, 24, 4, 12, 11, '5');
  rect(cells, 26, 10, 8, 4, '8');
  plot(cells, 27, 9, '8');
  plot(cells, 32, 9, '8');
  // Big sliding door with cross brace.
  rect(cells, 22, 26, 16, 21, '6');
  outlineRect(cells, 21, 25, 18, 22, '5');
  thickLine(cells, [22, 26], [37, 46], 1, '5');
  thickLine(cells, [37, 26], [22, 46], 1, '5');
  rect(cells, 21, 35, 18, 1, '5');
  // Side windows.
  for (const wx of [9, 46]) {
    rect(cells, wx, 27, 5, 6, '6');
    outlineRect(cells, wx - 1, 26, 7, 8, '5');
  }
  // Stone footing.
  rect(cells, 3, 47, 54, 5, '7');
  for (let x = 5; x < 55; x += 4) plot(cells, x + (x % 8 === 1 ? 1 : 0), 48 + (x % 3), '0');
  autoOutline(cells);
  return makeDef('barn', cells, BARN_SLOTS, [30, 51]);
}

// ---------------------------------------------------------------- shed

const SHED_SLOTS = ['outline', 'roofDark', 'roofLight', 'wallDark', 'wallMid', 'trim', 'inside', 'stone', 'hay'];
const SHED_PALETTE: PaletteInstance = {
  id: 'shed',
  colors: [0x241a11, 0x54452a, 0x6b5836, 0x6e5638, 0x8a6c46, 0xd8ccb2, 0x352818, 0x6a655c, 0xd9b45a],
};

function buildShed(): SpriteDef {
  const w = 40;
  const h = 34;
  const cells = toCells(blank(w, h));
  // Skillion roof, high on the left.
  for (let x = 1; x < 39; x += 1) {
    const y = 2 + Math.floor(x / 8);
    plot(cells, x, y, '2');
    plot(cells, x, y + 1, '2');
    plot(cells, x, y + 2, '1');
  }
  // Open front: posts + dark interior with feed sacks.
  rect(cells, 2, 9, 36, 21, '6');
  rect(cells, 2, 9, 3, 21, '4');
  rect(cells, 35, 9, 3, 21, '4');
  rect(cells, 2, 28, 36, 2, '4');
  // Sacks of feed.
  for (const [sx, sy, sw, sh] of [
    [8, 20, 7, 8],
    [16, 22, 7, 6],
    [24, 19, 7, 9],
  ] as const) {
    rect(cells, sx, sy, sw, sh, '8');
    rect(cells, sx + 1, sy + 1, sw - 2, 1, '5');
    plot(cells, sx + Math.floor(sw / 2), sy - 1, '8');
  }
  autoOutline(cells);
  return makeDef('feed-shed', cells, SHED_SLOTS, [20, 33]);
}

// ---------------------------------------------------------------- board

const BOARD_SLOTS = ['outline', 'postDark', 'postMid', 'panel', 'panelDark', 'paper', 'ink', 'roof'];
const BOARD_PALETTE: PaletteInstance = {
  id: 'board',
  colors: [0x211710, 0x5c4326, 0x7a5a34, 0x9c7a4a, 0x86663c, 0xefe8d2, 0x3a3226, 0x6e2f26],
};

function buildBoard(): SpriteDef {
  const w = 28;
  const h = 38;
  const cells = toCells(blank(w, h));
  rect(cells, 3, 2, 22, 3, '7'); // little roof cap
  rect(cells, 4, 5, 20, 18, '3');
  rect(cells, 4, 21, 20, 2, '4');
  outlineRect(cells, 3, 4, 22, 20, '1');
  // Pinned race bills.
  for (const [px, py, pw, ph] of [
    [6, 7, 6, 8],
    [14, 8, 7, 6],
    [7, 16, 7, 5],
    [16, 15, 6, 7],
  ] as const) {
    rect(cells, px, py, pw, ph, '5');
    for (let line = 1; line < ph - 1; line += 2) rect(cells, px + 1, py + line, pw - 2, 1, '6');
  }
  // Posts.
  rect(cells, 6, 24, 3, 13, '2');
  rect(cells, 19, 24, 3, 13, '2');
  rect(cells, 7, 24, 1, 13, '1');
  rect(cells, 20, 24, 1, 13, '1');
  autoOutline(cells);
  return makeDef('notice-board', cells, BOARD_SLOTS, [14, 37]);
}

// ---------------------------------------------------------------- gate

const GATE_SLOTS = ['outline', 'steelDark', 'steelMid', 'steelLight', 'plate', 'shadow'];
const GATE_PALETTE: PaletteInstance = {
  id: 'gate',
  colors: [0x1c1e24, 0x5c626e, 0x8a8f98, 0xb5bac2, 0xe8e2d2, 0x3a3e48],
};

function buildGate(): SpriteDef {
  const w = 76;
  const h = 42;
  const cells = toCells(blank(w, h));
  rect(cells, 2, 2, 72, 3, '2'); // top rail
  rect(cells, 2, 2, 72, 1, '3');
  for (let stall = 0; stall < 5; stall += 1) {
    const x = 3 + stall * 14;
    rect(cells, x, 4, 3, 34, '2');
    rect(cells, x, 4, 1, 34, '3');
    rect(cells, x + 4, 8, 9, 5, '1'); // padded bar
    rect(cells, x + 4, 20, 9, 3, '1');
    rect(cells, x + 5, 27, 7, 6, '4'); // number plate
    plot(cells, x + 8, 29, '0');
    plot(cells, x + 8, 30, '0');
  }
  rect(cells, 73, 4, 3, 34, '2');
  rect(cells, 73, 4, 1, 34, '3');
  rect(cells, 2, 38, 74, 2, '5');
  autoOutline(cells);
  return makeDef('starting-gate', cells, GATE_SLOTS, [38, 41]);
}

// ---------------------------------------------------------------- fence

const FENCE_SLOTS = ['outline', 'railLight', 'railShade', 'post'];
const FENCE_PALETTE: PaletteInstance = {
  id: 'fence',
  colors: [0x2a2118, 0xe8e2d2, 0xbdb4a0, 0xd8d0be],
};

function buildFence(): SpriteDef {
  const w = 24;
  const h = 14;
  const cells = toCells(blank(w, h));
  rect(cells, 0, 2, 24, 2, '1');
  rect(cells, 0, 3, 24, 1, '2');
  rect(cells, 0, 7, 24, 2, '1');
  rect(cells, 0, 8, 24, 1, '2');
  for (const px of [2, 20]) {
    rect(cells, px, 0, 3, 13, '3');
    rect(cells, px + 2, 0, 1, 13, '2');
  }
  autoOutline(cells);
  return makeDef('fence', cells, FENCE_SLOTS, [12, 13]);
}

// ---------------------------------------------------------------- trees

const TREE_SLOTS = ['outline', 'leafDark', 'leafMid', 'leafLight', 'trunkDark', 'trunkMid'];
const TREE_PALETTE: PaletteInstance = {
  id: 'tree',
  colors: [0x1c2413, 0x3f5c2a, 0x55793a, 0x6f9950, 0x4a3220, 0x6a4a2e],
};

function buildTree(big: boolean): SpriteDef {
  const w = big ? 34 : 24;
  const h = big ? 42 : 30;
  const cells = toCells(blank(w, h));
  const cx = Math.floor(w / 2);
  const r = big ? 9 : 6;
  // Trunk first so the canopy overlaps it.
  rect(cells, cx - 2, h - 14, 4, 13, '5');
  rect(cells, cx - 2, h - 14, 1, 13, '4');
  thickLine(cells, [cx - 1, h - 12], [cx - 5, h - 16], 2, '5');
  // Canopy: stacked discs, lit from the upper left.
  discFill(cells, cx - r + 2, r + 6, r, '1');
  discFill(cells, cx + r - 2, r + 7, r - 1, '1');
  discFill(cells, cx, r + 2, r, '2');
  discFill(cells, cx - 3, r + 1, r - 2, '3');
  for (let i = 0; i < (big ? 14 : 8); i += 1) {
    const px = 3 + scatter(i, 5, w - 6);
    const py = 3 + scatter(5, i, r * 2);
    if (cells[py]?.[px] === '2' || cells[py]?.[px] === '1') plot(cells, px, py, '3');
  }
  autoOutline(cells);
  return makeDef(big ? 'tree-big' : 'tree-small', cells, TREE_SLOTS, [cx, h - 1]);
}

// ---------------------------------------------------------------- clouds

const CLOUD_SLOTS = ['outline', 'body', 'shade'];
const CLOUD_PALETTE: PaletteInstance = {
  id: 'cloud',
  colors: [0x9aa4b8, 0xf2f4f2, 0xd6dce4],
};

function buildCloud(variant: number): SpriteDef {
  const w = variant === 0 ? 42 : 30;
  const h = 14;
  const cells = toCells(blank(w, h));
  const lumps = variant === 0 ? [8, 17, 27, 35] : [6, 14, 23];
  for (const [index, lx] of lumps.entries()) {
    discFill(cells, lx, 8 - (index % 2) * 2, 5 + (index % 2), '1');
  }
  for (let x = 2; x < w - 2; x += 1) {
    for (let y = 9; y < 13; y += 1) {
      if (cells[y]![x] === '1') plot(cells, x, y, '2');
    }
  }
  return makeDef(variant === 0 ? 'cloud-big' : 'cloud-small', cells, CLOUD_SLOTS, [
    Math.floor(w / 2),
    13,
  ]);
}

// ---------------------------------------------------------------- hurdle + flag

const HURDLE_SLOTS = ['outline', 'stripeA', 'stripeB', 'post'];
const HURDLE_PALETTE: PaletteInstance = {
  id: 'hurdle',
  colors: [0x241a11, 0xe8e2d2, 0xc4574a, 0x8a6c46],
};

function buildHurdle(): SpriteDef {
  const w = 12;
  const h = 24;
  const cells = toCells(blank(w, h));
  for (const px of [1, 9]) rect(cells, px, 4, 2, 19, '3');
  for (let x = 0; x < 12; x += 1) rect(cells, x, 0, 1, 4, Math.floor(x / 3) % 2 === 0 ? '1' : '2');
  rect(cells, 0, 12, 12, 2, '1');
  autoOutline(cells);
  return makeDef('hurdle', cells, HURDLE_SLOTS, [6, 23]);
}

const FLAG_SLOTS = ['outline', 'pole', 'flag', 'flagShade'];
const FLAG_PALETTE: PaletteInstance = {
  id: 'flag',
  colors: [0x241a11, 0xb8b2a2, 0xd9a441, 0xb0812e],
};

function buildFlagPost(): SpriteDef {
  const w = 12;
  const h = 26;
  const cells = toCells(blank(w, h));
  rect(cells, 2, 0, 2, 25, '1');
  for (let fy = 0; fy < 6; fy += 1) {
    rect(cells, 4, 1 + fy, Math.max(0, 7 - fy), 1, fy < 3 ? '2' : '3');
  }
  autoOutline(cells);
  return makeDef('flag-post', cells, FLAG_SLOTS, [3, 25]);
}

// ---------------------------------------------------------------- grandstand

const STAND_SLOTS = ['outline', 'canopyA', 'canopyB', 'frame', 'wall', 'crowdA', 'crowdB', 'crowdC', 'crowdD'];
const STAND_PALETTE: PaletteInstance = {
  id: 'grandstand',
  colors: [0x1d1712, 0xc4574a, 0xe8e2d2, 0x5c4a36, 0x8a7a5e, 0xd9a441, 0x6f8fd9, 0x7cc47c, 0xefe0c8],
};

function buildGrandstand(): SpriteDef {
  const w = 96;
  const h = 44;
  const cells = toCells(blank(w, h));
  // Striped canopy with scalloped edge.
  for (let x = 1; x < 95; x += 1) {
    const stripe = Math.floor((x - 1) / 8) % 2 === 0 ? '1' : '2';
    rect(cells, x, 2, 1, 8, stripe);
    if ((x - 1) % 8 < 4) plot(cells, x, 10, stripe);
  }
  rect(cells, 0, 1, 96, 1, '3');
  // Crowd: deterministic confetti of heads and shirts.
  rect(cells, 2, 12, 92, 18, '3');
  for (let y = 13; y < 29; y += 1) {
    for (let x = 3; x < 93; x += 1) {
      const pick = scatter(x, y, 9);
      if (pick < 4) plot(cells, x, y, String(5 + pick));
    }
  }
  // Support posts + front wall.
  for (const px of [4, 26, 48, 70, 90]) rect(cells, px, 10, 2, 24, '3');
  rect(cells, 1, 30, 94, 12, '4');
  rect(cells, 1, 30, 94, 2, '2');
  for (let x = 6; x < 92; x += 12) rect(cells, x, 34, 1, 8, '3');
  autoOutline(cells);
  return makeDef('grandstand', cells, STAND_SLOTS, [48, 43]);
}

// ---------------------------------------------------------------- ground tiles

const DIRT_SLOTS = ['outline', 'base', 'fleckDark', 'fleckLight'];
const DIRT_PALETTE: PaletteInstance = {
  id: 'dirt',
  colors: [0x241a11, 0x8a6a45, 0x79593a, 0x97754e],
};

function buildDirtTile(): SpriteDef {
  const w = 48;
  const h = 24;
  const cells = toCells(blank(w, h));
  rect(cells, 0, 0, w, h, '1');
  for (let i = 0; i < 46; i += 1) {
    const x = scatter(i, 3, w);
    const y = scatter(7, i, h);
    plot(cells, x, y, scatter(i, i, 2) === 0 ? '2' : '3');
    if (scatter(i, 11, 3) === 0) plot(cells, (x + 1) % w, y, '2');
  }
  return makeDef('dirt-tile', cells, DIRT_SLOTS, [0, 0]);
}

const GRASS_SLOTS = ['outline', 'base', 'bladeDark', 'bladeLight'];
const GRASS_PALETTE: PaletteInstance = {
  id: 'grass',
  colors: [0x1c2413, 0x5d7a44, 0x50693a, 0x6b8a4f],
};

function buildGrassTile(): SpriteDef {
  const w = 48;
  const h = 24;
  const cells = toCells(blank(w, h));
  rect(cells, 0, 0, w, h, '1');
  for (let i = 0; i < 40; i += 1) {
    const x = scatter(i, 13, w);
    const y = scatter(17, i, h);
    const char = scatter(i, 5, 2) === 0 ? '2' : '3';
    plot(cells, x, y, char);
    plot(cells, x + 1, y, char);
  }
  return makeDef('grass-tile', cells, GRASS_SLOTS, [0, 0]);
}

// ---------------------------------------------------------------- exports

export const BARN: Showpiece = { def: buildBarn(), palette: BARN_PALETTE };
export const FEED_SHED: Showpiece = { def: buildShed(), palette: SHED_PALETTE };
export const NOTICE_BOARD: Showpiece = { def: buildBoard(), palette: BOARD_PALETTE };
export const STARTING_GATE: Showpiece = { def: buildGate(), palette: GATE_PALETTE };
export const FENCE: Showpiece = { def: buildFence(), palette: FENCE_PALETTE };
export const TREE_BIG: Showpiece = { def: buildTree(true), palette: TREE_PALETTE };
export const TREE_SMALL: Showpiece = { def: buildTree(false), palette: TREE_PALETTE };
export const CLOUD_BIG: Showpiece = { def: buildCloud(0), palette: CLOUD_PALETTE };
export const CLOUD_SMALL: Showpiece = { def: buildCloud(1), palette: CLOUD_PALETTE };
export const HURDLE: Showpiece = { def: buildHurdle(), palette: HURDLE_PALETTE };
export const FLAG_POST: Showpiece = { def: buildFlagPost(), palette: FLAG_PALETTE };
export const GRANDSTAND: Showpiece = { def: buildGrandstand(), palette: STAND_PALETTE };
export const DIRT_TILE: Showpiece = { def: buildDirtTile(), palette: DIRT_PALETTE };
export const GRASS_TILE: Showpiece = { def: buildGrassTile(), palette: GRASS_PALETTE };

export const SCENERY_PIECES: Showpiece[] = [
  BARN,
  FEED_SHED,
  NOTICE_BOARD,
  STARTING_GATE,
  FENCE,
  TREE_BIG,
  TREE_SMALL,
  CLOUD_BIG,
  CLOUD_SMALL,
  HURDLE,
  FLAG_POST,
  GRANDSTAND,
  DIRT_TILE,
  GRASS_TILE,
];
